import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

// ── 15 standard body-map regions ──────────────────────────────────────────────
export const BODY_REGIONS = [
  { id: "head-neck",   num: 1,  label: "Head & Neck" },
  { id: "shoulder",    num: 2,  label: "Shoulder" },
  { id: "upper-arm",   num: 3,  label: "Upper Arm" },
  { id: "elbow",       num: 4,  label: "Elbow" },
  { id: "forearm",     num: 5,  label: "Forearm" },
  { id: "wrist-hand",  num: 6,  label: "Wrist & Hand" },
  { id: "chest-ribs",  num: 7,  label: "Chest & Ribs" },
  { id: "abdomen",     num: 8,  label: "Abdomen" },
  { id: "upper-back",  num: 9,  label: "Upper Back" },
  { id: "lower-back",  num: 10, label: "Lower Back" },
  { id: "hips-pelvis", num: 11, label: "Hips & Pelvis" },
  { id: "upper-leg",   num: 12, label: "Upper Leg / Thigh" },
  { id: "knee",        num: 13, label: "Knee" },
  { id: "lower-leg",   num: 14, label: "Lower Leg / Calf" },
  { id: "ankle-foot",  num: 15, label: "Ankle & Foot" },
] as const;

export type RegionId = (typeof BODY_REGIONS)[number]["id"];

// ── Image paths (placed in public/images/funnels/bpc157/) ─────────────────────
//   Drop the two files at:
//     artifacts/portfolio/public/images/funnels/bpc157/body-male.jpg
//     artifacts/portfolio/public/images/funnels/bpc157/body-female.jpg
//   Both must be 1024×635, front-facing standing figure in athletic wear.
//   If either file is missing, the component falls back to the vector illustration.
function imgUrl(sex: "male" | "female"): string {
  const base = import.meta.env.BASE_URL ?? "/";
  const prefix = base.endsWith("/") ? base : base + "/";
  return `${prefix}images/funnels/bpc157/body-${sex}.jpg`;
}

// ── Percentage-based hotspot coordinates ──────────────────────────────────────
//   Each entry has an array of [x%, y%] points (bilateral regions = 2 points).
//   `backRegion: true` marks the upper-back and lower-back approximations —
//   those two regions have no natural front-view location so they're placed at
//   the outer right edge of the torso silhouette as functional click targets.
//
//   Coordinates are in FULL-IMAGE percentage space (1024×635).  The actual
//   figure only spans roughly x: 40–60% of the frame, so the container crops
//   the white side margins with `object-cover` at a 2:3 portrait aspect ratio.
//   `toCropX()` remaps full-image x% into the cropped viewport; y is unchanged
//   because the full image height remains visible.
type PctPoint = [number, number]; // [x%, y%] in full-image space

const IMG_W = 1024;
const IMG_H = 635;
const CROP_ASPECT = 2 / 3; // container width / height
const CROP_VISIBLE_FRAC = (IMG_H * CROP_ASPECT) / IMG_W; // ≈ 0.4134 of image width
const CROP_LEFT_PCT = ((1 - CROP_VISIBLE_FRAC) / 2) * 100; // ≈ 29.3%
const toCropX = (x: number) => (x - CROP_LEFT_PCT) / CROP_VISIBLE_FRAC;

const HOTSPOTS_PCT: Record<RegionId, { points: PctPoint[]; backRegion?: true }> = {
  //                               viewer-left            viewer-right
  "head-neck":   { points: [[50, 11]] },
  "shoulder":    { points: [[44.5, 24],  [55.5, 24]] },
  "upper-arm":   { points: [[43, 33],    [57, 33]] },
  "elbow":       { points: [[42.5, 42],  [57.5, 42]] },
  "forearm":     { points: [[42, 50],    [58, 50]] },
  "wrist-hand":  { points: [[41.5, 58],  [58.5, 58]] },
  "chest-ribs":  { points: [[47, 30]] },                          // front, centre-left
  "abdomen":     { points: [[50, 42]] },                          // front, centre
  "upper-back":  { points: [[60, 28]], backRegion: true },        // beside right arm (front view)
  "lower-back":  { points: [[60.5, 42]], backRegion: true },      // beside right arm (front view)
  "hips-pelvis": { points: [[50, 52]] },
  "upper-leg":   { points: [[46.5, 60],  [53.5, 60]] },
  "knee":        { points: [[47, 70.5],  [53, 70.5]] },
  "lower-leg":   { points: [[47, 80],    [53, 80]] },
  "ankle-foot":  { points: [[47.5, 91],  [52.5, 91]] },
};

// ── SVG colour tokens (used by the vector fallback) ───────────────────────────
const SK  = "#F5D0A0";
const SKD = "#C8966A";
const CL  = "#0F766E";
const CLD = "#0A5C55";
const HR  = "#3B2A1A";
const EY  = "#2A1500";

// ── Vector fallback — Male ────────────────────────────────────────────────────
function MaleBody() {
  return (
    <g>
      <path fill={SK} stroke={SKD} strokeWidth="0.8"
        d="M48,82 Q42,100 41,126 Q39,150 40,160 Q39,168 43,172 L43,194 Q43,210 49,218 Q55,222 62,218 Q66,212 65,196 L64,174 Q68,168 68,160 Q69,148 68,126 Q68,100 62,82 Z"/>
      <path fill={SK} stroke={SKD} strokeWidth="0.8"
        d="M152,82 Q158,100 159,126 Q161,150 160,160 Q161,168 157,172 L157,194 Q157,210 151,218 Q145,222 138,218 Q134,212 135,196 L136,174 Q132,168 132,160 Q131,148 132,126 Q132,100 138,82 Z"/>
      <path fill={SK} stroke={SKD} strokeWidth="0.8"
        d="M68,240 Q62,270 63,300 Q61,312 67,316 Q62,350 64,378 Q64,392 71,403 Q79,410 92,408 Q100,404 100,396 Q100,384 97,378 Q93,350 95,318 Q100,312 100,300 Q102,270 100,240 Z"/>
      <path fill={SK} stroke={SKD} strokeWidth="0.8"
        d="M132,240 Q138,270 137,300 Q139,312 133,316 Q138,350 136,378 Q136,392 129,403 Q121,410 108,408 Q100,404 100,396 Q100,384 103,378 Q107,350 105,318 Q100,312 100,300 Q98,270 100,240 Z"/>
      <ellipse cx="79"  cy="32" rx="4"  ry="5.5" fill={SK} stroke={SKD} strokeWidth="0.8"/>
      <ellipse cx="121" cy="32" rx="4"  ry="5.5" fill={SK} stroke={SKD} strokeWidth="0.8"/>
      <ellipse cx="100" cy="32" rx="20" ry="25"  fill={SK} stroke={SKD} strokeWidth="0.8"/>
      <path fill={SK} stroke={SKD} strokeWidth="0.8" d="M93,55 L107,55 L109,70 Q100,73 91,70 Z"/>
      <path fill={CL} stroke={CLD} strokeWidth="0.8"
        d="M91,68 Q83,70 77,75 Q60,78 49,89 Q44,98 46,109 Q47,117 54,119 Q61,120 67,115 Q70,110 71,105 L71,184 L129,184 L129,105 Q130,110 133,115 Q139,120 146,119 Q153,117 154,109 Q156,98 151,89 Q140,78 123,75 Q117,70 109,68 Q104,66 100,65 Q96,66 91,68 Z"/>
      <path fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1"
        d="M91,68 Q96,66 100,65 Q104,66 109,68"/>
      <path fill={CL} stroke={CLD} strokeWidth="0.8"
        d="M71,183 Q65,192 64,206 Q63,220 67,230 Q71,240 78,244 L122,244 Q129,240 133,230 Q137,220 136,206 Q135,192 129,183 Z"/>
      <path fill={CLD} stroke="none" d="M71,183 L129,183 L129,191 Q100,193 71,191 Z"/>
      <path fill={HR} d="M79,27 Q79,7 100,8 Q121,7 121,27 Q117,16 100,15 Q83,16 79,27 Z"/>
      <ellipse cx="93"  cy="29" rx="2.5" ry="2.5" fill={EY}/>
      <ellipse cx="107" cy="29" rx="2.5" ry="2.5" fill={EY}/>
      <circle  cx="94"  cy="28" r="0.8"  fill="white"/>
      <circle  cx="108" cy="28" r="0.8"  fill="white"/>
      <path d="M87,23 Q93,21 98,23"   stroke={HR} fill="none" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M102,23 Q107,21 113,23" stroke={HR} fill="none" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M95,40 Q100,43 105,40"  stroke={SKD} fill="none" strokeWidth="1" strokeLinecap="round"/>
    </g>
  );
}

// ── Vector fallback — Female ──────────────────────────────────────────────────
function FemaleBody() {
  return (
    <g>
      <path fill={SK} stroke={SKD} strokeWidth="0.8"
        d="M55,86 Q49,114 49,156 Q48,166 52,170 L52,194 Q52,208 57,216 Q62,220 66,217 Q69,212 68,196 L67,172 Q70,166 69,158 Q69,118 63,88 Z"/>
      <path fill={SK} stroke={SKD} strokeWidth="0.8"
        d="M145,86 Q151,114 151,156 Q152,166 148,170 L148,194 Q148,208 143,216 Q138,220 134,217 Q131,212 132,196 L133,172 Q130,166 131,158 Q131,118 137,88 Z"/>
      <ellipse cx="79"  cy="32" rx="3.5" ry="5"   fill={SK} stroke={SKD} strokeWidth="0.8"/>
      <ellipse cx="121" cy="32" rx="3.5" ry="5"   fill={SK} stroke={SKD} strokeWidth="0.8"/>
      <ellipse cx="100" cy="32" rx="20"  ry="25"  fill={SK} stroke={SKD} strokeWidth="0.8"/>
      <path fill={SK} stroke={SKD} strokeWidth="0.8" d="M93,55 L107,55 L109,70 Q100,73 91,70 Z"/>
      <path fill={CL} stroke={CLD} strokeWidth="0.8"
        d="M68,192 Q62,202 61,216 Q59,232 63,244 Q57,276 59,300 Q57,312 63,316 Q59,350 61,378 Q61,392 68,404 Q76,410 90,408 Q98,404 100,396 Q101,386 97,378 Q94,350 96,318 Q100,312 100,300 Q102,276 100,244 Q100,232 100,192 Z"/>
      <path fill={CL} stroke={CLD} strokeWidth="0.8"
        d="M132,192 Q138,202 139,216 Q141,232 137,244 Q143,276 141,300 Q143,312 137,316 Q141,350 139,378 Q139,392 132,404 Q124,410 110,408 Q102,404 100,396 Q99,386 103,378 Q106,350 104,318 Q100,312 100,300 Q98,276 100,244 Q100,232 100,192 Z"/>
      <path fill={CLD} stroke="none" d="M68,190 L132,190 L132,200 Q100,202 68,200 Z"/>
      <path fill={CL} stroke={CLD} strokeWidth="0.8"
        d="M88,64 L88,88 Q80,90 74,96 Q68,102 68,110 L68,192 L132,192 L132,110 Q132,102 126,96 Q120,90 112,88 L112,64 Q109,61 107,60 Q104,59 100,59 Q96,59 93,60 Q90,61 88,64 Z"/>
      <path d="M88,64 L88,88"  stroke="rgba(255,255,255,0.20)" fill="none" strokeWidth="1"/>
      <path d="M112,64 L112,88" stroke="rgba(255,255,255,0.20)" fill="none" strokeWidth="1"/>
      <path fill={HR} d="M78,28 Q78,7 100,8 Q122,7 122,28 Q118,17 100,15 Q82,17 78,28 Z"/>
      <path fill={HR}
        d="M78,28 Q72,40 70,56 Q68,72 68,88 Q68,98 72,108 Q74,116 78,122 L83,122 Q80,116 77,107 Q74,97 74,88 Q74,72 76,56 Q78,42 84,30 Z"/>
      <path fill={HR}
        d="M122,28 Q128,40 130,56 Q132,72 132,88 Q132,98 128,108 Q126,116 122,122 L117,122 Q120,116 123,107 Q126,97 126,88 Q126,72 124,56 Q122,42 116,30 Z"/>
      <ellipse cx="93"  cy="29" rx="2.5" ry="2.5" fill={EY}/>
      <ellipse cx="107" cy="29" rx="2.5" ry="2.5" fill={EY}/>
      <circle  cx="94"  cy="28" r="0.8"  fill="white"/>
      <circle  cx="108" cy="28" r="0.8"  fill="white"/>
      <path d="M87,23 Q93,20 98,22"   stroke={HR} fill="none" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M102,22 Q107,20 113,23" stroke={HR} fill="none" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M95,40 Q100,43 105,40"  stroke={SKD} fill="none" strokeWidth="1" strokeLinecap="round"/>
    </g>
  );
}

// ── Vector fallback HOTSPOTS (SVG coordinate space 200×420) ──────────────────
const SVG_HOTSPOTS: Record<RegionId, {
  areas: [number, number, number, number][];
  at: [number, number];
}> = {
  "head-neck":   { areas: [[79, 7, 42, 68]],                                       at: [100, 18]  },
  "shoulder":    { areas: [[35, 66, 32, 48],   [133, 66, 32, 48]],                 at: [53, 84]   },
  "upper-arm":   { areas: [[35, 112, 32, 52],  [133, 112, 32, 52]],                at: [53, 132]  },
  "elbow":       { areas: [[35, 162, 32, 22],  [133, 162, 32, 22]],                at: [53, 172]  },
  "forearm":     { areas: [[36, 182, 30, 24],  [134, 182, 30, 24]],                at: [53, 193]  },
  "wrist-hand":  { areas: [[36, 204, 30, 26],  [134, 204, 30, 26]],                at: [53, 215]  },
  "chest-ribs":  { areas: [[70, 68, 30, 70]],                                      at: [80, 95]   },
  "abdomen":     { areas: [[70, 136, 30, 50]],                                     at: [80, 160]  },
  "upper-back":  { areas: [[100, 68, 30, 70]],                                     at: [120, 95]  },
  "lower-back":  { areas: [[100, 136, 30, 50]],                                    at: [120, 160] },
  "hips-pelvis": { areas: [[62, 184, 76, 64]],                                     at: [100, 212] },
  "upper-leg":   { areas: [[60, 244, 42, 62],  [98, 244, 42, 62]],                 at: [82, 273]  },
  "knee":        { areas: [[60, 304, 42, 26],  [98, 304, 42, 26]],                 at: [82, 316]  },
  "lower-leg":   { areas: [[61, 328, 40, 58],  [99, 328, 40, 58]],                 at: [82, 354]  },
  "ankle-foot":  { areas: [[61, 384, 40, 34],  [99, 384, 40, 34]],                 at: [82, 397]  },
};

// ── Props ─────────────────────────────────────────────────────────────────────
interface BodyDiagramProps {
  sex?: "male" | "female";
  mode?: "diagram" | "list";
  selectedRegions: string[];
  onRegionToggle: (regionId: string) => void;
}

// ── Image-based diagram ───────────────────────────────────────────────────────
interface ImageDiagramProps {
  sex: "male" | "female";
  selectedRegions: string[];
  onRegionToggle: (regionId: string) => void;
  onError: () => void;
  /** Region currently highlighted (hovered dot OR hovered legend row). */
  activeRegion: string | null;
  onActiveRegionChange: (regionId: RegionId | null) => void;
}

function ImageDiagram({
  sex,
  selectedRegions,
  onRegionToggle,
  onError,
  activeRegion,
  onActiveRegionChange,
}: ImageDiagramProps) {
  // Which specific dot shows its floating label pill.
  const [tooltip, setTooltip] = useState<{ id: RegionId; ptIdx: number } | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const src = imgUrl(sex);

  // On tap (mobile), flash the label pill briefly near the tapped dot.
  const flashTooltip = (id: RegionId, ptIdx: number) => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setTooltip({ id, ptIdx });
    flashTimer.current = setTimeout(() => {
      flashTimer.current = null;
      setTooltip(null);
    }, 1400);
  };

  return (
    <div className="w-full max-w-[320px] mx-auto">
      <div className="relative w-full" style={{ aspectRatio: "2 / 3" }}>
        {/* Base illustration */}
        <img
          src={src}
          alt={`${sex} body diagram — tap a region to select it`}
          className="absolute inset-0 w-full h-full object-cover rounded-md select-none"
          draggable={false}
          onError={onError}
        />

        {/* Hotspot dots — small, quiet markers with a generous 44px hit area */}
        {BODY_REGIONS.map((region) => {
          const id = region.id as RegionId;
          const def = HOTSPOTS_PCT[id];
          const sel = selectedRegions.includes(region.id);
          const active = activeRegion === region.id;
          const isBack = def.backRegion === true;

          return def.points.map(([px, py], ptIdx) => {
            const showPill =
              tooltip !== null && tooltip.id === id && tooltip.ptIdx === ptIdx;
            return (
              <button
                key={`${region.id}-${ptIdx}`}
                type="button"
                onClick={() => {
                  onRegionToggle(region.id);
                  flashTooltip(id, ptIdx);
                }}
                onMouseEnter={() => {
                  onActiveRegionChange(id);
                  setTooltip({ id, ptIdx });
                }}
                onMouseLeave={() => {
                  onActiveRegionChange(null);
                  setTooltip((t) =>
                    t && t.id === id && t.ptIdx === ptIdx && !flashTimer.current ? null : t
                  );
                }}
                onFocus={() => {
                  onActiveRegionChange(id);
                  setTooltip({ id, ptIdx });
                }}
                onBlur={() => {
                  onActiveRegionChange(null);
                  setTooltip(null);
                }}
                aria-label={`${region.label}${isBack ? " (approximate, back)" : ""}`}
                aria-pressed={sel}
                data-testid={`hotspot-img-${region.id}-${ptIdx}`}
                style={{ left: `${toCropX(px)}%`, top: `${py}%` }}
                className={cn(
                  // 44px invisible hit area, dot centered inside
                  "absolute w-11 h-11 -translate-x-1/2 -translate-y-1/2",
                  "rounded-full flex items-center justify-center",
                  "cursor-pointer focus:outline-none group",
                  showPill && "z-20"
                )}
              >
                {/* Visible marker dot */}
                <span
                  aria-hidden="true"
                  className={cn(
                    "rounded-full flex items-center justify-center",
                    "transition-all duration-150",
                    "group-focus-visible:ring-2 group-focus-visible:ring-teal-500 group-focus-visible:ring-offset-1",
                    sel
                      ? "w-[18px] h-[18px] bg-teal-700 border-2 border-white shadow-md"
                      : cn(
                          "w-4 h-4 bg-white/70 border-2 border-teal-700/80 shadow-sm",
                          isBack && "border-dashed"
                        ),
                    active &&
                      "scale-125 ring-4 ring-teal-400/40 border-teal-600 bg-white",
                    active && sel && "bg-teal-600"
                  )}
                >
                  {sel && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                </span>

                {/* Floating label pill — appears near the marker on hover/tap */}
                {showPill && (
                  <span
                    className={cn(
                      "absolute bottom-full mb-0.5 left-1/2 -translate-x-1/2",
                      "pointer-events-none whitespace-nowrap",
                      "bg-slate-900/85 text-white text-[11px] font-medium",
                      "px-2 py-0.5 rounded-full shadow"
                    )}
                  >
                    {region.label}
                  </span>
                )}
              </button>
            );
          });
        })}
      </div>

      {/* Quiet helper caption — below the artwork, not on top of it */}
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground text-center">
        Upper and lower back positions are approximate on this view — use the
        list if that's easier.
      </p>
    </div>
  );
}

// ── SVG-based diagram (fallback) ──────────────────────────────────────────────
function SvgDiagram({
  sex,
  selectedRegions,
  onRegionToggle,
}: {
  sex: "male" | "female";
  selectedRegions: string[];
  onRegionToggle: (regionId: string) => void;
}) {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);

  function NumberBadge({ num, cx, cy, selected }: {
    num: number; cx: number; cy: number; selected: boolean;
  }) {
    return (
      <g pointerEvents="none">
        <circle cx={cx} cy={cy} r={8.5}
          fill={selected ? "#065F46" : "#0F766E"} stroke="#fff" strokeWidth={1}/>
        <text x={cx} y={cy + 4} textAnchor="middle"
          fontSize={num >= 10 ? "7.5" : "8.5"} fontWeight="700"
          fill="#fff" fontFamily="system-ui, sans-serif">
          {num}
        </text>
      </g>
    );
  }

  return (
    <div className="flex-shrink-0 w-[180px] mx-auto sm:mx-0">
      <svg viewBox="0 0 200 420" width="180" height="378"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Body diagram — tap a region to select it">
        {sex === "male" ? <MaleBody /> : <FemaleBody />}
        <line x1="100" y1="70" x2="100" y2="184"
          stroke="rgba(255,255,255,0.35)" strokeWidth="1" strokeDasharray="3 3"
          pointerEvents="none"/>
        {(Object.entries(SVG_HOTSPOTS) as [RegionId, typeof SVG_HOTSPOTS[RegionId]][]).map(
          ([regionId, def]) =>
            def.areas.map(([x, y, w, h], areaIdx) => {
              const sel = selectedRegions.includes(regionId);
              const hov = hoveredRegion === regionId;
              return (
                <rect key={`${regionId}-${areaIdx}`}
                  x={x} y={y} width={w} height={h} rx="4"
                  fill={sel ? "rgba(15,118,110,0.30)" : hov ? "rgba(15,118,110,0.14)" : "transparent"}
                  stroke={sel ? "rgba(15,118,110,0.70)" : "transparent"}
                  strokeWidth="1" cursor="pointer"
                  tabIndex={0}
                  role="button"
                  aria-label={BODY_REGIONS.find((r) => r.id === regionId)?.label ?? regionId}
                  aria-pressed={sel}
                  onClick={() => onRegionToggle(regionId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRegionToggle(regionId);
                    }
                  }}
                  onFocus={() => setHoveredRegion(regionId)}
                  onBlur={() => setHoveredRegion(null)}
                  onMouseEnter={() => setHoveredRegion(regionId)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  className="focus:outline-none focus-visible:stroke-teal-600"
                  data-testid={`hotspot-${regionId}-${areaIdx}`}/>
              );
            })
        )}
        {BODY_REGIONS.map((region) => (
          <NumberBadge key={region.id} num={region.num}
            cx={SVG_HOTSPOTS[region.id as RegionId].at[0]}
            cy={SVG_HOTSPOTS[region.id as RegionId].at[1]}
            selected={selectedRegions.includes(region.id)}/>
        ))}
      </svg>
      <div className="h-6 mt-1 text-center">
        {hoveredRegion && (
          <span className="inline-block bg-teal-700 text-white px-2 py-0.5 rounded text-xs font-medium">
            {BODY_REGIONS.find((r) => r.id === hoveredRegion)?.label}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function BodyDiagram({
  sex = "male",
  mode = "diagram",
  selectedRegions,
  onRegionToggle,
}: BodyDiagramProps) {
  // Track whether the photo image loaded or errored.
  // Default: 'loading' — shows SVG while image loads, swaps on success, stays SVG on error.
  const [imgStatus, setImgStatus] = useState<"loading" | "loaded" | "error">("loading");

  // Two-way hover sync between the diagram dots and the legend list.
  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  // Pre-load the image in the background so we know quickly whether to use it.
  // The hidden <img> only fires onLoad / onError; the visible rendering is handled below.
  const preloadSrc = mode === "diagram" ? imgUrl(sex) : null;

  // ── List mode ──────────────────────────────────────────────────────────────
  if (mode === "list") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="body-diagram-list">
        {BODY_REGIONS.map((region) => (
          <div key={region.id} className="flex items-center space-x-2"
            data-testid={`checkbox-region-${region.id}`}>
            <Checkbox
              id={region.id}
              checked={selectedRegions.includes(region.id)}
              onCheckedChange={() => onRegionToggle(region.id)}
            />
            <Label htmlFor={region.id}
              className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {region.num}. {region.label}
            </Label>
          </div>
        ))}
      </div>
    );
  }

  // ── Diagram mode ───────────────────────────────────────────────────────────
  const useImageView = imgStatus === "loaded";

  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start"
      data-testid={`body-diagram-${sex}`}>

      {/* Hidden preloader — fires load/error to switch between SVG and image */}
      {preloadSrc && imgStatus !== "loaded" && (
        <img
          src={preloadSrc}
          className="hidden"
          aria-hidden="true"
          onLoad={() => setImgStatus("loaded")}
          onError={() => setImgStatus("error")}
        />
      )}

      {/* Left column: illustration + hotspots */}
      <div className={cn(
        "flex-shrink-0 mx-auto sm:mx-0",
        useImageView ? "w-full max-w-[400px]" : "w-[180px]",
      )}>
        {useImageView ? (
          <ImageDiagram
            sex={sex}
            selectedRegions={selectedRegions}
            onRegionToggle={onRegionToggle}
            onError={() => setImgStatus("error")}
            activeRegion={activeRegion}
            onActiveRegionChange={setActiveRegion}
          />
        ) : (
          <SvgDiagram
            sex={sex}
            selectedRegions={selectedRegions}
            onRegionToggle={onRegionToggle}
          />
        )}
      </div>

      {/* Right column: region legend (always rendered identically) */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-2.5 font-medium uppercase tracking-wide">
          Tap to select affected regions
        </p>
        <ol className="space-y-1.5">
          {BODY_REGIONS.map((region) => {
            const sel = selectedRegions.includes(region.id);
            return (
              <li key={region.id}>
                <button
                  type="button"
                  onClick={() => onRegionToggle(region.id)}
                  onMouseEnter={() => setActiveRegion(region.id)}
                  onMouseLeave={() => setActiveRegion(null)}
                  onFocus={() => setActiveRegion(region.id)}
                  onBlur={() => setActiveRegion(null)}
                  className={cn(
                    "flex items-center gap-2.5 w-full text-left px-2 py-1 rounded-md transition-colors text-sm",
                    sel
                      ? "bg-teal-50 text-teal-800 font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                    activeRegion === region.id && !sel && "bg-muted text-foreground",
                    activeRegion === region.id && sel && "bg-teal-100"
                  )}
                  aria-pressed={sel}
                  data-testid={`legend-${region.id}`}
                >
                  <span className={cn(
                    "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                    sel ? "bg-teal-700 text-white" : "bg-muted text-muted-foreground"
                  )}>
                    {region.num}
                  </span>
                  <span className="leading-tight">{region.label}</span>
                  {sel && (
                    <span className="ml-auto text-teal-600">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
